import React, { FC } from "react";
import { Button } from "@shared/components";
import { SuggestedCollaborator } from "@shared/models";
import { getFullName } from "@shared/utils";

// Relative, not the "@shared/components" alias: jest resolves no path aliases, and this component's
// own test needs InitialsAvatar's REAL rendered initials, not a stubbed marker -- same recipe as
// RankRow.tsx/PodiumTile.tsx (both cited in the phase doc) at the same folder depth.
import { InitialsAvatar } from "../../../../../shared/components/InitialsAvatar";
import { lastRecognisedCaption } from "../lastRecognisedCaption";
import "./index.scss";

interface CollaboratorCardProps {
  collaborator: SuggestedCollaborator;
  // The whole row, not a pre-built `Option` -- building the Recognise-form prefill is the caller's job
  // (SparkSpreadSection owns the `showOverlay` dispatch), matching RewardCard's own `onRedeem: (item) =>
  // void` precedent of a presentational card handing its whole subject back to its owning container.
  onRecognise: (collaborator: SuggestedCollaborator) => void;
}

/**
 * One "Spread your Sparks" card (923-core-context context/engineering/spark-rewards/phase-52-spread-your-sparks.md §3): a circular initials avatar, the
 * teammate's full name, a caption naming when the viewer last recognised them (or that they never
 * have), and a Recognise button. Purely presentational -- `SparkSpreadSection` owns the fetch and the
 * `showOverlay` dispatch this button's click ultimately triggers, the same split RewardCard/
 * RedeemContainer already use for a card-with-a-CTA.
 *
 * @param props.collaborator - the suggested teammate row (`user`, and `last_recognised_at` for the
 *   caption).
 * @param props.onRecognise - called with this card's own `collaborator` when Recognise is clicked.
 */
const CollaboratorCard: FC<CollaboratorCardProps> = (props) => {
  const { collaborator, onRecognise } = props;
  const fullName = getFullName(collaborator.user);

  return (
    <div className="collaborator-card" data-testid={`collaborator-card-${collaborator.user_id}`}>
      <InitialsAvatar
        firstName={collaborator.user.first_name}
        lastName={collaborator.user.last_name}
        size={40}
        imageUrl={collaborator.user.avatar_url}
      />
      <div className="collaborator-card-name">{fullName}</div>
      <div className="collaborator-card-caption">{lastRecognisedCaption(collaborator.last_recognised_at)}</div>
      <Button
        type="button"
        className="accent-solid collaborator-card-recognise-button"
        data-testid={`collaborator-card-recognise-${collaborator.user_id}`}
        aria-label={`Recognise ${fullName}`}
        onClick={() => onRecognise(collaborator)}
      >
        Recognise
      </Button>
    </div>
  );
};

export default CollaboratorCard;
